import classNames from 'classnames'
import React from 'react'

import {
  FormFocusTargetContext,
  IFormFocusTargetContext,
} from 'shared-libs/components/ui2/behavior/form-focus-target'
import { FocusTargetRegistrationProps } from 'shared-libs/components/ui2/behavior/focus-target-registration/focus-target-registration-props'
import { resolveBoundFieldPath } from 'shared-libs/components/ui2/fields/core-field/resolve-field-path'
import { UI2FormSectionIdContext } from 'shared-libs/components/ui2/forms/ui2-form-section/ui2-form-section-id-context'
import {
  IUI2WizardContext,
  UI2WizardContext,
} from 'shared-libs/components/ui2/forms/ui2-form/ui2-wizard-context'

import { IUI2PulseHighlightRef, UI2PulseHighlight } from '../pulse-highlight/pulse-highlight'

interface IProps extends FocusTargetRegistrationProps {
  className?: string
}

export class UI2FocusTargetRegistration extends React.Component<IProps> {
  public static contextType = FormFocusTargetContext as any
  declare context: IFormFocusTargetContext<HTMLElement> | undefined

  // Read via Consumers inside render (class components support only one contextType).
  private currentSectionId: string | undefined
  private currentWizardCtx: IUI2WizardContext | undefined

  private outerRef = React.createRef<HTMLDivElement>()
  private pulseRef = React.createRef<IUI2PulseHighlightRef>()
  private registeredPath: string | undefined

  public componentDidMount(): void {
    this.syncRegistration()
  }

  public componentDidUpdate(): void {
    this.syncRegistration()
  }

  public componentWillUnmount(): void {
    if (this.registeredPath && this.context?.unregister) {
      this.context.unregister(this.registeredPath)
    }
    this.registeredPath = undefined
  }

  public render(): React.ReactNode {
    const { children, className, borderRadius } = this.props
    return (
      <UI2FormSectionIdContext.Consumer>
        {(sectionId) => (
          <UI2WizardContext.Consumer>
            {(wizardCtx) => {
              const changed = sectionId !== this.currentSectionId
              this.currentSectionId = sectionId
              this.currentWizardCtx = wizardCtx as IUI2WizardContext
              if (changed && this.registeredPath) this.register(this.registeredPath)
              return (
                <div
                  ref={this.outerRef}
                  className={classNames('ui2-focusTarget', className)}
                  data-field-path={resolveBoundFieldPath(this.props)}
                >
                  <UI2PulseHighlight ref={this.pulseRef} borderRadius={borderRadius}>
                    {children}
                  </UI2PulseHighlight>
                </div>
              )
            }}
          </UI2WizardContext.Consumer>
        )}
      </UI2FormSectionIdContext.Consumer>
    )
  }

  private syncRegistration(): void {
    const nextPath = resolveBoundFieldPath(this.props)
    if (nextPath === this.registeredPath) return
    if (this.registeredPath && this.context?.unregister) {
      this.context.unregister(this.registeredPath)
    }
    this.registeredPath = nextPath
    if (nextPath) this.register(nextPath)
  }

  private register(path: string): void {
    this.context?.register?.(path, {
      getRef: () => this.outerRef.current,
      pulse: (intent) => this.pulseRef.current?.pulse(intent),
      hasError: () => !!this.props.hasError?.(),
      sectionId: this.currentSectionId,
      revealSection: this.revealSection,
    })
  }

  // The provider mounts above UI2Form and can't see the wizard context, so the
  // registered component owns the handoff. `revealSection`, not `jumpTo`, so a
  // save-flow submit can jump to a not-yet-visited section.
  private revealSection = (): void => {
    const ctx = this.currentWizardCtx
    const id = this.currentSectionId
    if (!ctx?.isWizard || !id) return
    if (ctx.activeSectionId === id) return
    ctx.revealSection(id)
  }
}
